import { onBeforeUnmount, onMounted, ref } from 'vue'
// types
import type { Ref } from 'vue'
import type { CardSliderProps } from '../types/interfaces'

interface CardSliderHookOptions {
  props: CardSliderProps
  onPrev: () => void
  onNext: () => void
}

interface CardSliderHookReturn {
  refRoot: Ref<HTMLElement | null>
  refTrack: Ref<HTMLElement | null>
  refCanScrollPrev: Ref<boolean>
  refCanScrollNext: Ref<boolean>
  handleClickPrev: () => void
  handleClickNext: () => void
}

export const useCardSlider = ({ props, onPrev, onNext }: CardSliderHookOptions): CardSliderHookReturn => {
  const refRoot = ref<HTMLElement | null>(null)
  const refTrack = ref<HTMLElement | null>(null)
  const refCanScrollPrev = ref<boolean>(false)
  const refCanScrollNext = ref<boolean>(false)

  let userInteracted = false

  const updateNavState = (): void => {
    const track = refTrack.value
    if (!track) return
    refCanScrollPrev.value = track.scrollLeft > 1
    refCanScrollNext.value = track.scrollLeft < track.scrollWidth - track.clientWidth - 1
  }

  const getScrollStep = (): number => {
    const track = refTrack.value
    if (!track) return 0
    const [first, second] = Array.from(track.children) as HTMLElement[]
    if (first && second) return second.offsetLeft - first.offsetLeft
    return first?.offsetWidth ?? track.clientWidth
  }

  const scrollByStep = (direction: 1 | -1): void => {
    refTrack.value?.scrollBy({ left: direction * getScrollStep(), behavior: 'smooth' })
  }

  const markInteracted = (): void => {
    userInteracted = true
  }

  const handleClickPrev = (): void => {
    if (!refCanScrollPrev.value) return
    markInteracted()
    scrollByStep(-1)
    onPrev()
  }

  const handleClickNext = (): void => {
    if (!refCanScrollNext.value) return
    markInteracted()
    scrollByStep(1)
    onNext()
  }

  // horizontal panning only — a vertical wheel over the track is the page scrolling,
  // not the user taking over the slider
  const handleTrackWheel = (event: WheelEvent): void => {
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) markInteracted()
  }

  const prefersReducedMotion = (): boolean => (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )

  const driveFromPageScroll = (): void => {
    const root = refRoot.value
    const track = refTrack.value
    if (!root || !track || userInteracted) return

    const maxOffset = track.scrollWidth - track.clientWidth
    if (maxOffset <= 0) return

    const rect = root.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const progress = (viewportHeight - rect.top) / (viewportHeight + rect.height)
    track.scrollLeft = Math.min(Math.max(progress, 0), 1) * maxOffset
  }

  const handleWindowScroll = (): void => {
    driveFromPageScroll()
  }

  onMounted(() => {
    const track = refTrack.value
    updateNavState()
    track?.addEventListener('scroll', updateNavState, { passive: true })
    track?.addEventListener('pointerdown', markInteracted, { passive: true })
    track?.addEventListener('wheel', handleTrackWheel, { passive: true })
    window.addEventListener('resize', updateNavState)

    if (props.scrollDriven && !prefersReducedMotion()) {
      window.addEventListener('scroll', handleWindowScroll, { passive: true })
      driveFromPageScroll()
    }
  })

  onBeforeUnmount(() => {
    const track = refTrack.value
    track?.removeEventListener('scroll', updateNavState)
    track?.removeEventListener('pointerdown', markInteracted)
    track?.removeEventListener('wheel', handleTrackWheel)
    window.removeEventListener('resize', updateNavState)
    window.removeEventListener('scroll', handleWindowScroll)
  })

  return {
    refRoot,
    refTrack,
    refCanScrollPrev,
    refCanScrollNext,
    handleClickPrev,
    handleClickNext,
  }
}
